import React from 'react';
import { Card, Tag, Typography, Space, Divider, Table, Tooltip } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button } from 'antd';

const { Title } = Typography;

const NotificationCard = styled(Card)`
  margin-bottom: 40px;
  border-radius: 20px;
  border: none;
  background: rgba(255, 255, 255, 0.13);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.10);
  position: relative;
  overflow: hidden;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #a0aec0;
  margin-bottom: 10px;
  margin-top: 18px;
  letter-spacing: 0.5px;
`;

const SummaryTable: React.FC<{ summary: any }> = ({ summary }) => {
  const { t } = useTranslation();
  const firstRow = summary && Object.values(summary)[0] as Record<string, any> | undefined;
  const columns = firstRow ? Object.keys(firstRow) : [];
  const summaryTitle = (() => {
    const label = t('agentnotify.summary');
    return label === 'agentnotify.summary' ? '对比摘要' : label;
  })();
  return (
    <Card size="small" style={{ borderRadius: 12, background: 'rgba(255,255,255,0.08)', marginTop: 8 }}>
      <SectionTitle>{summaryTitle}</SectionTitle>
      <table style={{ width: '100%', color: '#fff', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.10)' }}>
            <th style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>Product</th>
            {columns.map((k) => (
              <th key={k} style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(Object.entries(summary) as [string, Record<string, any>][])?.map(([product, criterias], idx) => {
            const row = (criterias ?? {}) as Record<string, any>;
            return (
              <tr key={product} style={{ background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.07)' }}>
                <td style={{ padding: 8 }}>{product}</td>
                {columns.map((col, i) => (
                  <td key={i} style={{ padding: 8 }}>{row[col]}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};

const getI18nLabel = (t: (s: string) => string, key: string) => {
  const label = t(`agentnotify.${key}`);
  return label === `agentnotify.${key}` ? key : label;
};

const renderHighlights = (highlights: any[], t: (s: string) => string) => (
  <div style={{ minWidth: 120, background: 'rgba(0, 123, 255, 0.06)', borderRadius: 6, padding: 6 }}>
    <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 2 }}>{t('agentnotify.highlights')}</div>
    <Space wrap>
      {highlights?.map((h, idx) => (
        <Tag key={idx} color="blue" style={{ marginBottom: 2 }}>
          <b>{h.label}</b>: {h.value} {h.unit}
        </Tag>
      ))}
    </Space>
  </div>
);

const renderNeededCriterias = (criterias: any[], t: (s: string) => string) => (
  <ol style={{ margin: 0, paddingLeft: 18, background: 'rgba(255, 193, 7, 0.06)', borderRadius: 6 }}>
    {criterias?.map((c, idx) => (
      <li key={idx} style={{ marginBottom: 2 }}>
        <Space size={4}>
          {Object.entries(c).map(([k, v]) => (
            <span key={k}><b>{getI18nLabel(t, k)}:</b> {String(v)}</span>
          ))}
        </Space>
      </li>
    ))}
  </ol>
);

const renderAppSpecific = (apps: any[], t: (s: string) => string) => (
  <div style={{ minWidth: 120 }}>
    {apps?.map((app, idx) => (
      <div key={idx} style={{ background: 'rgba(120,120,255,0.08)', borderRadius: 6, padding: 6, marginBottom: 4 }}>
        <div style={{ fontWeight: 600, color: '#6f42c1', marginBottom: 2 }}>
          <Tag color="purple" style={{ fontWeight: 600 }}>{app.app}</Tag>
        </div>
        {app.needed_criterias && renderNeededCriterias(app.needed_criterias, t)}
      </div>
    ))}
  </div>
);

const renderCell = (value: any, key: string, t: (s: string) => string) => {
  if (key === 'highlights' && Array.isArray(value)) return renderHighlights(value, t);
  if (key === 'app_specific' && Array.isArray(value)) return renderAppSpecific(value, t);
  if (Array.isArray(value)) {
    return (
      <Space wrap>
        {value.map((v, idx) =>
          typeof v === 'object'
            ? <Tooltip key={idx} title={JSON.stringify(v)}><Tag color="geekblue">{idx + 1}</Tag></Tooltip>
            : <Tag key={idx}>{String(v)}</Tag>
        )}
      </Space>
    );
  }
  if (typeof value === 'object' && value !== null) {
    return <Tooltip title={JSON.stringify(value)}><Tag color="orange">{t('agentnotify.object')}</Tag></Tooltip>;
  }
  if (typeof value === 'string' && value.startsWith('http')) {
    return <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>{t('agentnotify.detail')}</a>;
  }
  return String(value ?? '');
};

const getAllKeys = (items: any[]) => {
  const keys = new Set<string>();
  items.forEach(item => {
    Object.keys(item || {}).forEach(k => keys.add(k));
  });
  // 主字段优先
  const main = ['main_image', 'product_name', 'brand', 'model', 'score', 'url', 'highlights', 'app_specific'];
  const rest = Array.from(keys).filter(k => !main.includes(k));
  return [...main.filter(k => keys.has(k)), ...rest];
};

const ProductTable: React.FC<{ items: any[] }> = ({ items }) => {
  const { t } = useTranslation();
  const keys = getAllKeys(items);
  const columns = keys.map(key => ({
    title: getI18nLabel(t, key),
    dataIndex: key,
    key,
    render: (value: any) => renderCell(value, key, t),
    width: key === 'main_image' ? 60 : undefined,
    align: key === 'main_image' ? ('center' as const) : undefined,
  }));

  return (
    <Table
      columns={columns}
      dataSource={items.map((item, idx) => ({ ...item, key: idx }))}
      pagination={false}
      scroll={{ x: Math.max(900, keys.length * 120) }}
      bordered
      size="middle"
      style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, margin: '16px 0' }}
    />
  );
};

// 新增 ProductList 组件
const ProductList: React.FC<{ items: any[] }> = ({ items }) => {
  const { t } = useTranslation();
  if (!Array.isArray(items) || items.length === 0) return null;
  const keys = getAllKeys(items);
  // 主字段
  const mainFields = ['product_name', 'brand', 'model', 'score'];
  // 其他字段
  const otherFields = keys.filter(k => !['main_image', ...mainFields, 'highlights', 'app_specific', 'url'].includes(k));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, margin: '16px 0' }}>
      {items.map((item, idx) => (
        <Card
          key={idx}
          size="small"
          style={{ borderRadius: 16, background: 'rgba(255,255,255,0.10)', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
          styles={{ body: { padding: 20 } }}
        >
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {/* 左侧主图 */}
            {item.main_image && (
              <div style={{ minWidth: 90, maxWidth: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={item.main_image} alt={item.product_name || 'product'} style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, background: '#fff' }} />
              </div>
            )}
            {/* 右侧内容 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* 主字段 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>{item.product_name}</span>
                {item.brand && <Tag color="blue" style={{ fontWeight: 500 }}>{item.brand}</Tag>}
                {item.model && <Tag color="geekblue" style={{ fontWeight: 500 }}>{item.model}</Tag>}
                {item.score !== undefined && <Tag color="gold" style={{ fontWeight: 600 }}>{t('agentnotify.score')}: {item.score}</Tag>}
              </div>
              {/* 其他字段 */}
              {otherFields.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                  {otherFields.map((key) => (
                    item[key] !== undefined && item[key] !== null && item[key] !== '' && (
                      <div key={key} style={{ minWidth: 100 }}>
                        <span style={{ color: '#a0aec0', fontWeight: 500 }}>{getI18nLabel(t, key)}: </span>
                        {renderCell(item[key], key, t)}
                      </div>
                    )
                  ))}
                </div>
              )}
              {/* 高亮/标签/应用特定/详情链接 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 8 }}>
                {item.highlights && renderHighlights(item.highlights, t)}
                {item.app_specific && renderAppSpecific(item.app_specific, t)}
                {item.url && typeof item.url === 'string' && item.url.startsWith('http') && (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', fontWeight: 500, marginLeft: 8 }}>{t('agentnotify.detail')}</a>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const ProductSearchNotification: React.FC<{ content: any }> = ({ content }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = React.useState<'table' | 'list'>('table');
  if (!content) return null;
  // 只处理业务内容部分，不解构 isRead、time、uid
  const {
    title,
    Items = [],
    summary,
    comments,
    statistics,
    behind_the_scene,
    show_feedback_options
  } = content;

  const safeTitle = typeof title === 'string' ? title : (title ? String(title) : t('agentnotify.result'));
  const safeStatistics = statistics && typeof statistics === 'object' && !Array.isArray(statistics) ? statistics : undefined;
  const safeComments = Array.isArray(comments) ? comments : [];
  const safeBehindTheScene = typeof behind_the_scene === 'string' ? behind_the_scene : '';
  const safeShowFeedback = !!show_feedback_options;

  return (
    <NotificationCard>
      {/* 标题和切换视图按钮 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <Title level={4} style={{ color: '#fff', margin: 0 }}>{safeTitle}</Title>
        {Array.isArray(Items) && Items.length > 0 && (
          <Button
            icon={viewMode === 'table' ? <TableOutlined /> : <UnorderedListOutlined />}
            onClick={() => setViewMode(viewMode === 'table' ? 'list' : 'table')}
            size="small"
            title={viewMode === 'table' ? t('agentnotify.table_view') : t('agentnotify.list_view')}
          />
        )}
      </div>
      {/* 统计信息独立一行 */}
      {safeStatistics && (
        <div style={{ marginBottom: 8 }}>
          <Space wrap>
            {Object.entries(safeStatistics).map(([k, v]) => (
              <Tag key={k} color="blue" style={{ fontSize: 12 }}>{getI18nLabel(t, k)}: {String(v)}</Tag>
            ))}
          </Space>
        </div>
      )}
      <Divider style={{ margin: '12px 0', borderColor: 'rgba(255,255,255,0.13)' }} />

      {/* 产品表格/列表分区 */}
      {Array.isArray(Items) && Items.length > 0 && (
        <>
          <SectionTitle>{t('agentnotify.result')}</SectionTitle>
          {viewMode === 'table' ? <ProductTable items={Items} /> : <ProductList items={Items} />}
        </>
      )}

      {/* Summary 区域 */}
      {summary && (
        <div style={{ margin: '24px 0 0 0' }}>
          <SummaryTable summary={summary} />
        </div>
      )}

      {/* Comments 区域 */}
      {safeComments.length > 0 && (
        <div style={{ margin: '24px 0 0 0' }}>
          <Card size="small" style={{ borderRadius: 12, background: 'rgba(255,255,255,0.08)' }}>
            <SectionTitle>{t('agentnotify.comments')}</SectionTitle>
            <ul style={{ margin: 0, padding: '8px 0 0 18px', color: '#fff' }}>
              {safeComments.map((c: any, idx: number) => (
                <li key={idx}>{typeof c === 'string' ? c : JSON.stringify(c)}</li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {/* Behind the Scene/Feedback */}
      {(safeBehindTheScene || safeShowFeedback) && (
        <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap', borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 16 }}>
          {safeBehindTheScene && <a href={safeBehindTheScene} target="_blank" rel="noopener noreferrer" style={{ color: '#aaa', fontSize: 13 }}>{t('agentnotify.behind_the_scene')}</a>}
          {safeShowFeedback && <Tag color="red">{t('agentnotify.feedback')}</Tag>}
        </div>
      )}
    </NotificationCard>
  );
};

export default ProductSearchNotification; 